import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from "@/components/ui/toaster"
import DebugIndicator from './components/DebugIndicator'
import Layout from './components/Layout'
import AccessDenied from './components/AccessDenied'
import BillsPage from './pages/BillsPage'
import Jobs from './pages/Jobs.tsx'
import ConfigPage from './pages/ConfigPage'
import Costs from './pages/Costs.tsx'
import OrganizationManagementPage from './pages/admin/OrganizationManagementPage'
import WorkspaceManagementPage from './pages/admin/WorkspaceManagementPage'
import DatasourceBillsPage from './pages/admin/DatasourceBillsPage'
import { useEffect, useMemo } from 'react'
import { AuthProvider } from './context/AuthContext'
import ProtectedAdminRoute from './components/ProtectedAdminRoute'
import { useAuth } from './context/AuthContext'
import { GlobalJobsApiProvider } from '@umd-dit/jobs-component-react'
import { createAPI } from '@umd-dit/oidc-core'

// Import the OIDC web component to register it
import '@umd-dit/react-auth'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5000,
      refetchOnWindowFocus: false,
      placeholderData: (previousData: unknown) => previousData,
      throwOnError: (error: Error) => {
        return error?.message?.includes('401') || error?.message?.includes('403')
      }
    },
    mutations: {
      retry: 1,
      retryDelay: 1000
    }
  }
})

function PreserveQueryParamsRoute() {
  const location = useLocation()
  return <Navigate to={`/bills${location.search}`} replace />
}

// Routing component 
function WorkspaceAwareRouting() {
  const {
    isAuthenticated,
    isLoading,
    loadingWorkspaces,
    hasWorkspaceAccess
  } = useAuth();

  // If loading auth or workspaces, show loading
  if (isLoading || loadingWorkspaces) {
    return (
      <div className="container mx-auto p-6 flex justify-center items-center h-screen">
        <div className="text-center">
          <p className="text-xl font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // If authenticated but no workspace access, show access denied
  if (isAuthenticated && !hasWorkspaceAccess) {
    return <AccessDenied/>;
  }

  // Render the normal routes
  return (
    <Routes>
      <Route path="/" element={<PreserveQueryParamsRoute/>}/>

      {/* Main Application Layout with Workspace-Specific Pages */}
      <Route element={<Layout/>}>
        <Route path="bills" element={<BillsPage/>}/>
        <Route path="jobs" element={<Jobs/>}/>
        <Route path="costs" element={<Costs/>}/>
        <Route path="config" element={<ConfigPage/>}/>

        {/* Admin Section */}
        <Route path="admin/management" element={
          <ProtectedAdminRoute>
            <OrganizationManagementPage/>
          </ProtectedAdminRoute>
        }/>
        <Route path="admin/organizations/:org_id/workspaces" element={
          <ProtectedAdminRoute>
            <WorkspaceManagementPage/>
          </ProtectedAdminRoute>
        }/>
        <Route path="admin/datasource" element={
          <ProtectedAdminRoute>
            <DatasourceBillsPage/>
          </ProtectedAdminRoute>
        }/>

        <Route path="/" element={<BillsPage/>}/>
      </Route>

      <Route path="*" element={<Navigate to="/" replace/>}/>
    </Routes>
  );
}

// Simple protected route component to use temporarily
function ProtectedRoute({children}: {children: React.ReactNode}) {
  const { isAuthenticated, isLoading, hasWorkspaceAccess } = useAuth();

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white/90">
        <div className="w-24 h-24 border-8 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Check if user has workspace access
  if (!hasWorkspaceAccess) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}

export default function App() {
  useEffect(() => {
    // Check for debug flag in URL on initial load
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === 'true') {
      console.log('[App] Debug mode activated via URL parameter');
      sessionStorage.setItem('debugModeEnabled', 'true');
    }

    // Initialize centralized API instances
    try {
      console.log('[App] Initializing centralized API instances');
      
      // Create legislative review API instance
      if (import.meta.env.VITE_API_URL) {
        createAPI('legislative', import.meta.env.VITE_API_URL);
        console.log('[App] Legislative Review API instance created');
      }

      // Costs API will be created when needed in the Costs component
      
    } catch (error) {
      console.error('[App] Error initializing API instances:', error);
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  // Create jobs API instance for GlobalJobsApiProvider
  const jobsApiInstance = useMemo(() => {
    if (import.meta.env.VITE_JOBS_API_URL) {
      return createAPI('jobs', import.meta.env.VITE_JOBS_API_URL);
    }
    return null;
  }, []);

  console.log('Legislative Review Client ID is ' + import.meta.env.VITE_OIDC_CLIENT_ID);

  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <umd-auth
          api-url={import.meta.env.VITE_API_URL}
          authority={import.meta.env.VITE_OIDC_AUTHORITY}
          client-id={import.meta.env.VITE_OIDC_CLIENT_ID}
          devmode={import.meta.env.VITE_OIDC_DEVMODE}
          debug-mode={import.meta.env.DEV ? "true" : "false"}
          id="oidc-auth-component"
          onAccessGranted={(e: CustomEvent) => {
            console.log('[App] Access granted event received:', e);
            // Also dispatch auth-state-change event for our AuthContext
            document.dispatchEvent(new CustomEvent('auth-state-change', {
              detail: e.detail,
              bubbles: true
            }));
            // Also dispatch a simple event to notify auth success
            window.dispatchEvent(new Event('oidc-auth-success'));
          }}
        ></umd-auth>

        <AuthProvider>
          {jobsApiInstance ? (
            <GlobalJobsApiProvider apiInstance={jobsApiInstance}>
              <ProtectedRoute>
                <WorkspaceAwareRouting />
              </ProtectedRoute>
              <Toaster />
              <DebugIndicator />
            </GlobalJobsApiProvider>
          ) : (
            <div className="flex items-center justify-center h-screen">
              <p className="text-red-600">Failed to initialize Jobs API. Please check VITE_JOBS_API_URL configuration.</p>
            </div>
          )}
        </AuthProvider>
      </QueryClientProvider>
    </Router>
  );
}